import { useMemo } from 'react';
import { CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getQuickDateOptions, toFormDateStr } from '@/lib/events/quickDates';
import type { UseFormReturn } from 'react-hook-form';
import type { EventFormData } from '@/components/events/eventFormSchema';

interface QuickDatePickerProps {
  form: UseFormReturn<EventFormData>;
}

/**
 * Shows quick-date chips ("Idag", "I morgon", "Nästa lördag", etc.)
 * that pre-fill the event_date field. Only shown for new events.
 */
export function QuickDatePicker({ form }: QuickDatePickerProps) {
  const options = useMemo(() => getQuickDateOptions(), []);
  const currentDate = form.watch('event_date');

  const handleSelect = (date: Date) => {
    const dateStr = toFormDateStr(date);
    form.setValue('event_date', dateStr, { shouldDirty: true, shouldTouch: true });
    form.setValue('start_date', dateStr, { shouldDirty: false });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <CalendarDays className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">När händer det?</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const dateStr = toFormDateStr(opt.date);
          const isSelected = currentDate === dateStr;
          return (
            <Button
              key={dateStr}
              type="button"
              variant={isSelected ? 'default' : 'outline'}
              size="sm"
              className="h-8 text-xs"
              data-testid="event-quick-date-option"
              onClick={() => handleSelect(opt.date)}
            >
              {opt.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
